import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { AccountAppCommand } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import {
	CommonState,
	RequestOtpPayload,
	TwoFactorAuthAction,
	TwoFactorAuthCodeBEVerificationErrors,
	TwoFactorAuthCodeCommonVerificationErrors,
	twoFactorAuthCodeVerificationErrorDictionary,
	TwoFactorAuthCodeVerificationErrors,
	TwoFactorAuthConfigService,
	TwoFactorAuthDevice,
	TwoFactorAuthEnabledMethods,
	TwoFactorAuthFlow,
	TwoFactorAuthMethodType,
	TwoFactorAuthRemoveType,
	TwoFactorAuthSelector,
	TwoFactorAuthState,
	TwoFactorAuthTelemetryService,
	TwoFactorVerifyPayload,
} from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CODES,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	LoginType,
	MessageService,
	MessageType,
	OperatingSystemService,
	PlatformName,
	SESSION_ACTION_TYPE,
	SessionAction,
	SessionSelector,
	TwoFactorAuthMethod,
} from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { VerificationCodeComponent } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { last } from "lodash-es";
import { BehaviorSubject, Observable, Subscription, combineLatest, from, iif, merge, of, timer } from "rxjs";
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	scan,
	shareReplay,
	startWith,
	switchMap,
	take,
	takeWhile,
	tap,
	withLatestFrom,
} from "rxjs/operators";

import { OTP_CREDENTIAL_ERROR_CODES } from "./verification-code-container.const";
import { ErrorMessage, OtpCredential, OtpCredentialRequestOptions } from "./verification-code-container.model";

@Component({
	selector: "obg-m-verification-code-container",
	templateUrl: "verification-code-container.component.html",
	styleUrls: ["./verification-code-container.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class VerificationCodeContainerComponent implements OnInit, AfterViewInit, OnDestroy {
	@HostBinding("class.obg-m-verification-code") hostCssClass = true;
	@ViewChild("submitButton", { read: ElementRef }) submitButton!: ElementRef;
	@ViewChild(VerificationCodeComponent) otpInput!: VerificationCodeComponent;

	@Input() authFlow!: TwoFactorAuthFlow;
	@Input() forceMethod: TwoFactorAuthMethod | undefined;
	@Input() hideRememberDevice = false;
	@Input() suspiciousFlow = false;
	@Input() enabledMethods: TwoFactorAuthEnabledMethods[] | undefined;
	@Input() removeMethodType: TwoFactorAuthRemoveType | undefined;
	@Input() accountNotVerified = false;
	@Input() xAuthToken: string | undefined;
	@Input() newPhoneNumber: string | undefined;
	@Input() disableOtpMethodSwitching = false;

	@Output() back = new EventEmitter<void>();
	@Output() close = new EventEmitter<void>();

	readonly headerTitle$ = this.store.select(TwoFactorAuthSelector.getVerificationDialogHeaderTitle);
	readonly iconDetails$ = this.store.select(TwoFactorAuthSelector.getIconDetails);
	readonly changeMethodText$ = this.store.select(TwoFactorAuthSelector.getChangeMethodText);
	readonly areBothMethodsEnabled$ = this.store.select(TwoFactorAuthSelector.getAreBothMethodsEnabled);
	readonly buttonText$ = this.store.select(TwoFactorAuthSelector.getButtonText);
	readonly retryCooldownSeconds = 30;
	readonly attemptNumberRemainWarning = 3;
	readonly isAllowedToUseVerification$ = new BehaviorSubject(false);
	readonly changeProfileErrorMessage$ = new BehaviorSubject<ErrorMessage>({});

	otpForm = this.formBuilder.nonNullable.group({
		otp: "",
		rememberDevice: false,
	});
	isRetryEnabled$ = new BehaviorSubject(false);
	retryCount$ = new BehaviorSubject(0);
	timer$: Observable<number> | undefined;
	errorMessage$: Observable<ErrorMessage> | undefined;
	isLoading$ = new BehaviorSubject(false);
	isVerifyButtonDisabled$: Observable<boolean> | undefined;
	showChangeMethodButton$: Observable<boolean> | undefined;
	isChangeMethodButtonDisabled$: Observable<boolean> | undefined;
	isChangePhoneNumberButtonEnabled$: Observable<boolean> | undefined;
	genericLoginErrors$: Observable<string> | undefined;
	showResendButtonAndCounter$: Observable<boolean> | undefined;
	verificationAttemptAvailable: number | undefined;
	method: TwoFactorAuthMethod | undefined;
	customerContactInfo$ = this.store.select(TwoFactorAuthSelector.getCustomerContactInfo);
	isSubmitButtonDisabled = true;
	isOtpTextAuthStateEnabled = this.twoFactorAuthConfigService.getIsOtpTextAuthStateEnabled();
	jurisdiction: Jurisdiction | undefined;

	private readonly isIOS: boolean | undefined;
	private readonly isAndroid: boolean | undefined;
	private isSideEffectAllowed = false;
	private username: string | undefined;
	private currentDevice: TwoFactorAuthDevice | undefined;
	private shouldRememberUser: boolean | undefined;
	private subscription$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState & TwoFactorAuthState>,
		private changeDetectorRef: ChangeDetectorRef,
		private sessionAction: SessionAction,
		private messageService: MessageService,
		private formBuilder: FormBuilder,
		private sessionSelector: SessionSelector,
		private operatingSystemService: OperatingSystemService,
		private twoFactorAuthConfigService: TwoFactorAuthConfigService,
		private twoFactorAuthTelemetryService: TwoFactorAuthTelemetryService,
		private documentRef: DocumentRef
	) {
		this.isIOS = this.operatingSystemService.getOperatingSystem() === PlatformName.IOS;
		this.isAndroid = this.operatingSystemService.getOperatingSystem() === PlatformName.Android;
	}

	ngOnInit(): void {
		if (this.newPhoneNumber) {
			this.customerContactInfo$ = of(this.newPhoneNumber);
		}
		if (this.forceMethod) {
			this.store.dispatch(
				TwoFactorAuthAction.setSelectedMethod({
					method: this.forceMethod,
				})
			);
		}
		if (this.authFlow !== TwoFactorAuthFlow.ChangePhoneFlow) {
			const payload: RequestOtpPayload = {
				authFlow: this.authFlow,
				isPostRegistration: this.accountNotVerified,
			};

			this.store.dispatch(TwoFactorAuthAction.requestOTP({ payload }));
		}
		this.store.dispatch(TwoFactorAuthAction.loadCurrentDevice());

		this.timer$ = this.isRetryEnabled$.pipe(
			filter((enabled) => !enabled),
			map(() => {
				let count = this.retryCount$.getValue();
				count++;
				return count;
			}),
			tap((count) => this.retryCount$.next(count)),
			switchMap((count) =>
				timer(0, 1000).pipe(
					scan((seconds) => --seconds, this.retryCooldownSeconds * count),
					tap((seconds) => seconds === 0 && this.isRetryEnabled$.next(true)),
					takeWhile((seconds) => seconds >= 0)
				)
			)
		);

		this.genericLoginErrors$ = this.messageService
			.consume$({
				context: [SESSION_ACTION_TYPE.login, SESSION_ACTION_TYPE.loginWithTwoFactorAuth],
				messageType: MessageType.Error,
				excludeCodes: [AUTH_MESSAGE_CODES.login.termsAndConditionsNotAccepted],
			})
			.pipe(
				distinctUntilChanged(),
				map((messages) => {
					const lastMessage = last(messages);

					// when we get another 2FA error with Biometric channel, we need to close this modal
					if (lastMessage?.messageCode === AUTH_MESSAGE_CODES.login.twoFactorAuthenticationRequired) {
						const challengeDetails = (lastMessage?.data as any)?.challengeDetails;

						if (
							challengeDetails?.communicationMethods[0]?.channel === "Biometric" ||
							challengeDetails?.primaryCommunicationMethod.channel === "Biometric"
						) {
							this.close.emit();
						}
					}

					return lastMessage?.messageCode ?? "";
				}),
				shareReplay()
			);

		const jurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			tap((jurisdiction) => (this.jurisdiction = jurisdiction))
		);

		const verificationAttemptAvailable$ = iif(
			() => this.isLoginFlow && !this.accountNotVerified,
			this.store.select(this.sessionSelector.getRemainingAttempts()),
			this.store.select(TwoFactorAuthSelector.getRemainingTokenRequests)
		).pipe(
			tap((verificationAttemptAvailable) => (this.verificationAttemptAvailable = verificationAttemptAvailable))
		);

		this.showResendButtonAndCounter$ = combineLatest([
			this.store.select(TwoFactorAuthSelector.isSuspiciousLogin),
			verificationAttemptAvailable$.pipe(startWith(5)),
			this.selectTheErrorCodeByLoginFlow$().pipe(startWith("")),
		]).pipe(
			map(
				([isSuspiciousLogin, verificationAttemptAvailable, errorCode]) =>
					(isSuspiciousLogin || verificationAttemptAvailable !== undefined) &&
					!this.isMaxAttemptReached(errorCode)
			)
		);

		this.showChangeMethodButton$ = combineLatest([
			this.store.select(TwoFactorAuthSelector.getAreBothMethodsEnabled),
			of(
				(this.isLoginFlow && !this.forceMethod) ||
					this.removeMethodType === TwoFactorAuthRemoveType.RemoveBothMethod
			),
		]).pipe(
			map(([isBothMethodEnabled, isEnvironmentEngageChange]) => {
				return (
					this.twoFactorAuthConfigService.getIsChangeMethodButtonEnabled() ||
					(isBothMethodEnabled && isEnvironmentEngageChange)
				);
			})
		);

		this.isChangePhoneNumberButtonEnabled$ = combineLatest([
			this.store.select(TwoFactorAuthSelector.getSelectedMethod),
			of(this.authFlow === TwoFactorAuthFlow.SetupMethodFlow),
		]).pipe(
			map(
				([selectedMethod, isSetupFlow]) =>
					selectedMethod === TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod && isSetupFlow
			)
		);

		const isOtpInputDisabled$ = combineLatest([
			verificationAttemptAvailable$.pipe(
				map((verificationAttemptAvailable) => !this.isLoginFlow && !verificationAttemptAvailable)
			),
			this.isLoading$,
			this.selectTheErrorCodeByLoginFlow$(),
		]).pipe(
			map(
				([verificationAttemptAvailable, isLoading, errorCode]) =>
					verificationAttemptAvailable || isLoading || this.isMaxAttemptReached(errorCode)
			),
			tap((isDisabled) => (isDisabled ? this.otpForm.disable() : this.otpForm.enable()))
		);

		this.isVerifyButtonDisabled$ = combineLatest([
			this.otpForm.statusChanges.pipe(startWith("INVALID")),
			isOtpInputDisabled$,
		]).pipe(map(([otpStatus, otpInputDisabled]) => otpStatus === "INVALID" || otpInputDisabled));

		this.errorMessage$ = this.getErrorMessageObservable$().pipe(
			tap(() => {
				if (!this.isSideEffectAllowed) {
					return;
				}

				this.isLoading$.next(false);

				setTimeout(() => {
					this.otpForm.controls.otp.setErrors({
						backendError: true,
					});
				}, 1);
				this.isSideEffectAllowed = false;
				this.changeDetectorRef.markForCheck();
			})
		);

		this.isChangeMethodButtonDisabled$ = combineLatest([
			this.showChangeMethodButton$,
			verificationAttemptAvailable$.pipe(map((verificationAttemptAvailable) => !!verificationAttemptAvailable)),
			this.selectTheErrorCodeByLoginFlow$().pipe(startWith("")),
			this.isRetryEnabled$,
		]).pipe(
			map(([showChangeButton, verificationAttemptAvailable, errorCode, isRetryEnabled]) => {
				return (
					!showChangeButton ||
					!verificationAttemptAvailable ||
					this.isMaxAttemptReached(errorCode) ||
					!isRetryEnabled
				);
			})
		);

		const credentials$ = combineLatest([
			this.store.select(CustomerManagementSelector.getCredentials),
			this.store.select(CustomerManagementSelector.getCustomer),
			this.store.select(this.sessionSelector.getRememberMe()),
		]).pipe(
			ignoreNil(),
			tap(([credentials, customer, isRememberMe]) => {
				this.username = customer?.subscriptions?.email ?? credentials?.username;
				this.shouldRememberUser = isRememberMe;
			})
		);

		const isTwoFactorAuthLoading$ = this.store.select(TwoFactorAuthSelector.getIsBusy).pipe(
			ignoreNil(),
			tap((isBusy) => this.isLoading$.next(isBusy))
		);

		const currentDevice$ = this.store.select(TwoFactorAuthSelector.getCurrentDevice).pipe(
			ignoreNil(),
			tap((currentDevice) => (this.currentDevice = currentDevice))
		);

		const method$ = this.store.select(TwoFactorAuthSelector.getSelectedMethod).pipe(
			ignoreNil(),
			tap((method) => (this.method = method)),
			tap((method) => {
				if (this.accountNotVerified) {
					this.twoFactorAuthTelemetryService.registrationOpenVerificationCodeContainer({
						method: this.method!,
					});
				} else if (this.authFlow === TwoFactorAuthFlow.ChangePhoneFlow) {
					this.twoFactorAuthTelemetryService.myAccountOpenVerificationCodeContainer({ method: this.method! });
				} else {
					this.twoFactorAuthTelemetryService.openVerificationCodeContainer({
						authFlow: this.authFlow,
						method: method!,
						removeMethodType: this.removeMethodType,
						suspiciousFlow: this.suspiciousFlow,
					});
				}
			})
		);

		this.subscription$$ = merge(
			credentials$,
			currentDevice$,
			isTwoFactorAuthLoading$,
			isOtpInputDisabled$,
			jurisdiction$,
			verificationAttemptAvailable$,
			this.genericLoginErrors$
		).subscribe(() => this.changeDetectorRef.markForCheck());

		this.subscription$$.add(method$.subscribe());
	}

	ngAfterViewInit(): void {
		this.handleSmsOtpCode();
		this.otpInput.focus();
	}

	ngOnDestroy() {
		this.store.dispatch(TwoFactorAuthAction.setIsBusy({ isBusy: false }));
		this.subscription$$?.unsubscribe();
	}

	private handleSmsOtpCode() {
		if (!this.isOtpCredentialSupported()) {
			return;
		}

		const otpCredential$ = this.getOtpCredentialCode$().subscribe(
			(otpCredentialCode: string | undefined) => {
				if (otpCredentialCode) {
					this.otpInput.handleOtpFill(otpCredentialCode);
				}
			},
			(error) => {
				console.error(`${error}`);
			}
		);

		this.subscription$$?.add(otpCredential$);
	}

	private getOtpCredentialCode$(): Observable<string | undefined> {
		const otpCredentialRequestOptions: OtpCredentialRequestOptions = {
			otp: { transport: ["sms"] },
		};

		return from(navigator.credentials.get(otpCredentialRequestOptions)).pipe(
			map((otpCredential: OtpCredential | null) => otpCredential?.code?.toString()),
			catchError(
				(error: any) =>
					new Observable<string | undefined>((observer) => {
						error.name !== OTP_CREDENTIAL_ERROR_CODES.abortError
							? observer.error(`Getting OTP credentials: ${error}`)
							: observer.complete();
					})
			)
		);
	}

	private isOtpCredentialSupported(): boolean {
		return "OTPCredential" in window;
	}

	onChangeMethodClick() {
		if (this.accountNotVerified) {
			this.twoFactorAuthTelemetryService.registrationChangeMethodClicked({ method: this.method! });
		} else {
			this.twoFactorAuthTelemetryService.changeMethodClicked({
				authFlow: this.authFlow,
				method: this.method!,
				removeMethodType: this.removeMethodType,
				suspiciousFlow: this.suspiciousFlow,
			});
		}

		this.store.dispatch(TwoFactorAuthAction.toggleSelectedMethod());
		this.otpForm.controls.otp.setValue("");
		this.otpForm.controls.otp.setErrors(null);
		this.otpInput.focus();
		this.resendCode();
	}

	onResendCodeButtonClicked() {
		if (this.accountNotVerified) {
			this.twoFactorAuthTelemetryService.registrationResendCodeButtonClicked({ method: this.method! });
		} else if (this.authFlow === TwoFactorAuthFlow.ChangePhoneFlow) {
			this.twoFactorAuthTelemetryService.myAccountResendCodeButtonClicked({ method: this.method! });
		} else {
			this.twoFactorAuthTelemetryService.resendCodeButtonClicked({
				authFlow: this.authFlow,
				method: this.method!,
				removeMethodType: this.removeMethodType,
				suspiciousFlow: this.suspiciousFlow,
			});
		}

		this.resendCode();
	}

	private resendCode() {
		this.isRetryEnabled$.next(false);
		if (this.authFlow === TwoFactorAuthFlow.ChangePhoneFlow && this.newPhoneNumber && this.xAuthToken) {
			this.documentRef.obgEventBus?.dispatch(AccountAppCommand.NewPhoneVerificationRequestSmsCode, {
				phoneNumber: this.newPhoneNumber,
				headers: { "x-auth-token": this.xAuthToken },
			});
		} else {
			this.store.dispatch(
				TwoFactorAuthAction.retryRequestOTP({
					payload: { authFlow: this.authFlow, isPostRegistration: this.accountNotVerified },
				})
			);
		}
	}

	customerSupportOnClick() {
		this.close.emit();
	}

	onRememberDeviceChanged({ checked }: MatSlideToggleChange) {
		this.twoFactorAuthTelemetryService.rememberDeviceChanged({
			authFlow: this.authFlow,
			method: this.method!,
			removeMethodType: this.removeMethodType,
		});
		this.otpForm.controls.rememberDevice.setValue(checked);
	}

	verify() {
		this.isLoading$.next(true);
		this.isAllowedToUseVerification$.next(true);
		this.isSideEffectAllowed = true;

		if (this.accountNotVerified) {
			this.twoFactorAuthTelemetryService.submitRegistrationVerificationSuccess({ method: this.method! });
		} else if (this.authFlow === TwoFactorAuthFlow.ChangePhoneFlow) {
			this.twoFactorAuthTelemetryService.submitMyAccountVerificationSuccess({ method: this.method! });
		} else {
			this.twoFactorAuthTelemetryService.submitVerificationCodeContainer({
				authFlow: this.authFlow,
				method: this.method!,
				removeMethodType: this.removeMethodType,
				suspiciousFlow: this.suspiciousFlow,
			});
		}

		switch (this.authFlow) {
			case TwoFactorAuthFlow.ChangePhoneFlow:
				if (!!this.otpForm.value?.otp && this.xAuthToken) {
					this.documentRef.obgEventBus?.dispatch(AccountAppCommand.NewPhoneVerificationVerifyCode, {
						smsCode: this.otpForm.value.otp,
						onSuccess: () => {
							this.documentRef.obgEventBus?.dispatch(
								AccountAppCommand.NewPhoneVerificationVerifyCodeSuccess
							);
						},
						onError: (error: { code: TwoFactorAuthCodeVerificationErrors; remainingAttempts: number }) => {
							this.changeProfileErrorMessage$.next({
								genericMessage: this.handleErrorMessage(
									error.code as TwoFactorAuthCodeVerificationErrors
								),
							});
							this.verificationAttemptAvailable = error.remainingAttempts;
						},
						headers: { "x-auth-token": this.xAuthToken },
					});
				}

				// Call the eventbus with the necessary calls
				// notify the tsx file somehow to submit the currentcustomer request
				break;
			case TwoFactorAuthFlow.SetupMethodFlow:
				/**
				 * The `current-customer-security-options` endpoint is disabled for BetssonBR
				 * Instead, a patch request is made to the `authenticationchallenges` endpoint
				 */
				if (!this.twoFactorAuthConfigService.getCurrentCustomerSecurityOptionsEnabled()) {
					this.store
						.select(CustomerManagementSelector.getCustomerId)
						.pipe(
							take(1),
							map((customerId) => {
								if (!this.otpForm.value?.otp || !customerId) {
									throw new Error(
										"OTP missing OR Customer ID OR Login Token - verification of token."
									);
								}
								const payload: TwoFactorVerifyPayload = {
									customerReference: customerId,
									customerReferenceType: "CustomerGuid",
									operation: "TwoFactorVerification",
									verificationToken: this.otpForm.value.otp,
								};

								this.store.dispatch(
									TwoFactorAuthAction.verifyOTPAccountNotVerified({
										twoFactorVerifyPayload: payload,
									})
								);
							})
						)
						// eslint-disable-next-line rxjs/no-ignored-subscription
						.subscribe();
				} else {
					// The `current-customer-security-options` endpoint is enabled for Mobilbahis
					this.store.dispatch(
						TwoFactorAuthAction.operateAuthMethod({
							operationType: TwoFactorAuthMethodType.Setup,
							token: this.otpForm.value?.otp,
						})
					);
				}

				break;
			case TwoFactorAuthFlow.RemoveMethodFlow:
				const operationType =
					this.removeMethodType === TwoFactorAuthRemoveType.RemoveBothMethod
						? TwoFactorAuthMethodType.RemoveAll
						: TwoFactorAuthMethodType.Remove;
				this.store.dispatch(
					TwoFactorAuthAction.operateAuthMethod({
						operationType,
						token: this.otpForm.value?.otp,
					})
				);
				break;
			case TwoFactorAuthFlow.LoginFlow:
				const rememberDevice = this.otpForm.value?.rememberDevice;

				this.store.dispatch(TwoFactorAuthAction.enableCurrentDevice({ isEnabled: !!rememberDevice }));
				if (!this.otpForm.value?.otp) {
					throw new Error("OTP missing - cannot perform login with two factor auth.");
				}

				if (this.accountNotVerified) {
					this.store
						.select(this.sessionSelector.getAccountNotVerifiedTwoFADetails())
						.pipe(
							take(1),
							tap((details) => {
								if (!this.otpForm.value?.otp || !details?.customerId || !details?.loginToken) {
									throw new Error(
										"OTP missing OR Customer ID OR Login Token - verification of token."
									);
								}
								const payload: TwoFactorVerifyPayload = {
									customerReference: details.customerId,
									customerReferenceType: "CustomerGuid",
									operation: "RegistrationVerification",
									verificationToken: this.otpForm.value.otp,
									loginToken: details.loginToken,
								};

								this.store.dispatch(
									TwoFactorAuthAction.verifyOTPAccountNotVerified({ twoFactorVerifyPayload: payload })
								);
							})
						)
						// eslint-disable-next-line rxjs/no-ignored-subscription
						.subscribe();
				} else {
					this.store.dispatch(
						this.sessionAction.loginWithTwoFactorAuthentication({
							verificationCode: this.otpForm.value.otp,
							type: this.suspiciousFlow ? LoginType.SuspiciousLogin : LoginType.VerificationCode,
							method: this.method!,
							username: this.username ?? "",
							rememberDevice,
							deviceId: this.currentDevice?.id,
							shouldRememberUser: this.shouldRememberUser,
							platform: this.platform(),
						})
					);
				}

				break;
		}
	}

	private platform(): string {
		let platformLogin = "Website";
		if (this.isIOS) {
			platformLogin = "Ios";
		}
		if (this.isAndroid) {
			platformLogin = "Android";
		}
		return platformLogin;
	}

	submitWithEnter() {
		if (this.otpForm.valid) {
			this.verify();
		}
	}

	submitWithPasteCompleted() {
		this.isSubmitButtonDisabled = false;
		setTimeout(() => this.submitButton.nativeElement.click(), 10);
	}

	focusSubmitButtonOnNextTick() {
		setTimeout(() => this.submitButton.nativeElement.focus(), 10);
		this.isSubmitButtonDisabled = false;
	}

	setCodeIncomplete() {
		this.isSubmitButtonDisabled = true;
	}

	/**
	 * BetssonBR is the only brand which currently handles dynamic verification
	 * code text based on user authentication state for both 2FA methods
	 */
	get verificationCodeKey(): string {
		if (!this.isOtpTextAuthStateEnabled) {
			return "two-factor-auth.dialog.code-change-text";
		}

		const loginStatus = this.isLoginFlow ? "logged-out" : "logged-in";
		const method = this.method === TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod ? "sms" : "email";

		return `two-factor-auth.dialog-verification-code-text.${loginStatus}.${method}`;
	}

	get isLoginFlow() {
		return this.authFlow === TwoFactorAuthFlow.LoginFlow;
	}

	get showVerificationAttempts() {
		return (
			this.verificationAttemptAvailable && this.verificationAttemptAvailable <= this.attemptNumberRemainWarning
		);
	}

	trackVerificationCodeChange() {
		if (this.accountNotVerified) {
			this.twoFactorAuthTelemetryService.trackRegistrationVerificationCodeChange({ method: this.method! });
		} else if (this.authFlow === TwoFactorAuthFlow.ChangePhoneFlow) {
			this.twoFactorAuthTelemetryService.trackMyAccountVerificationCodeChange({ method: this.method! });
		} else {
			this.twoFactorAuthTelemetryService.trackVerificationCodeChange({
				authFlow: this.authFlow,
				method: this.method!,
				removeMethodType: this.removeMethodType,
				suspiciousFlow: this.suspiciousFlow,
			});
		}
	}

	private handleErrorMessage(errorCode: TwoFactorAuthCodeVerificationErrors): string {
		if (!errorCode) {
			return "";
		}

		const errorValue = twoFactorAuthCodeVerificationErrorDictionary[errorCode];

		if (errorValue) {
			this.disableSubmitButtonIfInvalidToken(errorValue);
			return errorValue;
		}

		return this.isLoginFlow ? `login.${errorCode.toLowerCase()}` : errorCode;
	}

	private disableSubmitButtonIfInvalidToken(errorValue: string): void {
		const invalidTokenErrors = [
			twoFactorAuthCodeVerificationErrorDictionary.E_SECURITYOPTIONS_INVALID2FATOKEN,
			twoFactorAuthCodeVerificationErrorDictionary.E_AUTHENTICATIONCHALLENGES_INVALIDTOKEN,
		];

		if (invalidTokenErrors.includes(errorValue)) {
			this.isSubmitButtonDisabled = true;
		}
	}

	private getErrorMessageObservable$(): Observable<ErrorMessage> {
		return merge(
			this.genericLoginErrors$!.pipe(
				map((messageCode) => {
					if (
						messageCode ===
							TwoFactorAuthCodeBEVerificationErrors.E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED ||
						(!this.verificationAttemptAvailable &&
							messageCode === TwoFactorAuthCodeBEVerificationErrors.E_SESSIONS_LOGIN_INVALIDCREDENTIALS)
					) {
						return this.handleMaxAttemptsReachedError(true);
					}

					return {
						genericMessage: this.handleErrorMessage(messageCode as TwoFactorAuthCodeVerificationErrors),
					};
				})
			),
			this.store.select(TwoFactorAuthSelector.getErrorCode).pipe(
				withLatestFrom(this.isAllowedToUseVerification$),
				map(([twoFactorAuthError, isRequestSent]) => {
					if (
						twoFactorAuthError ===
						TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED
					) {
						return this.handleMaxAttemptsReachedError(isRequestSent);
					}

					return {
						genericMessage: this.handleErrorMessage(
							twoFactorAuthError as TwoFactorAuthCodeVerificationErrors
						),
					};
				})
			),
			this.changeProfileErrorMessage$
		);
	}

	private handleMaxAttemptsReachedError(isRequestSent: boolean) {
		this.isSubmitButtonDisabled = true;
		this.otpForm.controls.otp.disable();
		return {
			...(isRequestSent &&
				this.isLoginFlow && {
					genericMessage: this.handleErrorMessage(
						TwoFactorAuthCodeCommonVerificationErrors.MaxLoginAttemptReached
					),
				}),
			customMessage: this.handleErrorMessage(
				this.isLoginFlow
					? TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED
					: TwoFactorAuthCodeCommonVerificationErrors.MaxLoginAttemptReachedSetupFlow
			),
		};
	}

	private selectTheErrorCodeByLoginFlow$(): Observable<string | undefined> {
		return iif(
			() => this.isLoginFlow,
			this.genericLoginErrors$ ?? of(undefined),
			this.store.select(TwoFactorAuthSelector.getErrorCode)
		);
	}

	private isMaxAttemptReached(errorCode: string | undefined): boolean {
		return (
			errorCode === TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED ||
			errorCode === TwoFactorAuthCodeBEVerificationErrors.E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED ||
			!this.verificationAttemptAvailable
		);
	}
}
